// workshop.model.ts

export type WorkshopMode = 'InPerson' | 'Video' | 'Hybrid';
export type WorkshopStatus = 'Draft' | 'Published' | 'Full' | 'Completed' | 'Canceled';
export type WorkshopSessionStatus = 'Scheduled' | 'InProgress' | 'Completed' | 'Canceled' | 'Postponed' | 'Draft';

export interface Workshop {
  workshopId: number;
  title: string;
  description: string;
  topic: string;
  language: string;
  mode: WorkshopMode;
  instructorName: string | null;
  status: WorkshopStatus;

  // Session-level (flattened from the active/relevant session)
  workshopSessionId: number;
  sessionStartDateTime: Date;
  sessionEndDateTime: Date;
  //timestamp uses the epoch time - we are sorting off of this in the datatable date column
  sessionStartTimestamp: number;
  videoLink: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  room: string | null;
  capacityMax: number | null;
  waitlistCap: number | null;
  registrationCloseDate: string | null;
  cancellationReason: string | null;
  sessionStatus: WorkshopSessionStatus;

  // Registration-level (computed/derived)
  registeredCount: number;
  seatsRemaining: number | null;
  isRegistered: boolean;
  currentUserRegistrationStatus: string | null;
}

export interface WorkshopApiResponse {
  workshop: {
    workshopId: number;
    instructorName: string | null;
    title: string;
    description: string;
    topic: string;
    language: string;
    mode: string;
    addressLine1: string | null;
    addressLine2: string | null;
    city: string | null;
    state: string | null;
    zipCode: string | null;
    videoLink: string | null;
    workshopStartDateTime: string;
    workshopEndDateTime: string;
    capacityMax: number | null;
    waitlistCap: number | null;
    recurrenceRule: string | null;
    status: string;
    createdDate: string;
    createdBy: number;
    modifiedDate: string | null;
    modifiedBy: number | null;
  };
  workshopSessionRegistrationDetails: Array<{
    workshopSession: {
      workshopSessionId: number;
      workshopId: number;
      sessionStartDateTime: string;
      sessionEndDateTime: string;
      addressLine1: string | null;
      addressLine2: string | null;
      city: string | null;
      state: string | null;
      zipCode: string | null;
      room: string | null;
      videoLink: string | null;
      capacityMax: number | null;
      waitlistCap: number | null;
      registrationCloseDate: string | null;
      instructorName: string | null;
      status: string;
      comments: string | null;
      cancellationReason: string | null;
      cancellationDate: string | null;
      createdDate: string;
      createdBy: number;
      modifiedDate: string | null;
      modifiedBy: number | null;
    };
    workshopRegistrationsDetails: {
      workshopSessionId: number;
      sessionRegisteredRegistrationsCount: number;
      isCurrentUserRegistered: boolean;
      currentUserRegistrationStatus: string | null;
    };
  }>;
}

export function mapWorkshopsFromApi(responses: WorkshopApiResponse[]): Workshop[] {
  return responses.flatMap(({ workshop, workshopSessionRegistrationDetails }) =>
    workshopSessionRegistrationDetails.map(({ workshopSession, workshopRegistrationsDetails }) => {
      const cap = workshopSession.capacityMax;
      const registered = workshopRegistrationsDetails.sessionRegisteredRegistrationsCount;
      return {
        workshopId: workshop.workshopId,
        title: workshop.title,
        description: workshop.description,
        topic: workshop.topic,
        language: workshop.language,
        mode: workshop.mode as WorkshopMode,
        instructorName: workshopSession.instructorName ?? workshop.instructorName,
        status: workshop.status as WorkshopStatus,
        workshopSessionId: workshopSession.workshopSessionId,
        sessionStartDateTime: new Date(workshopSession.sessionStartDateTime),
        sessionEndDateTime: new Date(workshopSession.sessionEndDateTime),
        sessionStartTimestamp: new Date(workshopSession.sessionStartDateTime).getTime(),
        videoLink: workshopSession.videoLink,
        addressLine1: workshopSession.addressLine1,
        addressLine2: workshopSession.addressLine2,
        city: workshopSession.city,
        state: workshopSession.state,
        zipCode: workshopSession.zipCode,
        room: workshopSession.room,
        capacityMax: cap,
        waitlistCap: workshopSession.waitlistCap,
        registrationCloseDate: workshopSession.registrationCloseDate,
        cancellationReason: workshopSession.cancellationReason,
        sessionStatus: workshopSession.status as WorkshopSessionStatus,
        registeredCount: registered,
        seatsRemaining: cap !== null ? Math.max(0, cap - registered) : null,
        isRegistered: workshopRegistrationsDetails.isCurrentUserRegistered,
        currentUserRegistrationStatus: workshopRegistrationsDetails.currentUserRegistrationStatus,
      } satisfies Workshop;
    })
  ).filter(w => w.status !== 'Draft' && w.sessionStatus != 'Draft').sort((a, b) => a.sessionStartTimestamp - b.sessionStartTimestamp);
}

